import { useCallback, useEffect, useState, useMemo, type FC, useRef, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  parseToInquiryStage,
  useInquiryList,
  useInquirySummary,
  type Inquiry,
  type InquiryStage
} from "../../hooks/use-inquiries";
import { inquiryStages } from "../../hooks/use-inquiries/constants";
import { formatDateString } from "../forms/form-helpers";
import {
  type SortConfig,
  type SortDirection,
  SortableTable,
  type SortableTableHeader,
  type UsePaginatedSorterHook
} from "../table";
import { usePaginator } from "../pagination";
import { useDynamicPageSize } from "../table/use-dynamic-page-size";
import { useInquiryStatus } from "../../hooks/use-web-socket/use-inquiry-status";
import { useAccessControl } from "../../app/use-access-control";
import { useCenterConstants, useCenters } from "../../hooks/use-center";
import { Confirm, ConfirmHandle } from "../confirm/confirm";
import { useAccessToken } from "../../hooks/use-access-token";
import { useMyProfile } from "../../hooks/use-my-profile";
import { getStaffMemberPermissions } from "../../hooks/use-staff";
import { useQueryClient } from "@tanstack/react-query";
import {
  inquiryColumnFilterSizerClassName,
  MultiCheckboxColumnFilter,
  StageStartDateRangeFilter
} from "./inquiry-column-filters";

/** Default `stagesCount` before the list query resolves. */
const EMPTY_STAGES_COUNT = Object.freeze(
  Object.fromEntries(inquiryStages.map(s => [s, 0])) as Record<InquiryStage, number>
);

const headerWithFilter = (label: string, control: ReactNode | null): ReactNode => (
  <span className="inquiry-column-header-inner">
    <span className="inquiry-column-header-label">{label}</span>
    {control ?? (
      <span className={`${inquiryColumnFilterSizerClassName} invisible user-select-none`} aria-hidden="true" />
    )}
  </span>
);

export interface InquiryListProps {
  preselectedInquiryId: string | undefined;
  search?: string;
  onInquirySelect?: (inquiry: Inquiry) => void;
  onStatusChange?: () => void;
  tableRef?: React.RefObject<HTMLTableElement>;
  /** When set with `onSortByChange`, sort state is controlled by the parent (e.g. toolbar next to search). */
  sortBy?: string;
  onSortByChange?: (nextSortBy: string) => void;
}

export const InquiryList: FC<InquiryListProps> = ({
  preselectedInquiryId,
  search,
  onInquirySelect,
  onStatusChange,
  tableRef,
  sortBy: sortByProp,
  onSortByChange
}) => {
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry>();
  const [hasInitialData, setHasInitialData] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [sortByInternal, setSortByInternal] = useState("student_last_name");
  const sortControlled = sortByProp !== undefined && onSortByChange !== undefined;
  const sortBy = sortControlled ? sortByProp! : sortByInternal;

  const commitSortBy = useCallback(
    (next: string | ((previous: string) => string)) => {
      if (sortControlled && onSortByChange) {
        const resolved = typeof next === "function" ? next(sortByProp!) : next;
        onSortByChange(resolved);
      } else {
        setSortByInternal(prev => (typeof next === "function" ? next(prev) : next));
      }
    },
    [sortControlled, onSortByChange, sortByProp]
  );

  const { limit } = useDynamicPageSize({ utilityHeight: 90, tableRowHeight: 45 });

  const [searchParams, setSearchParams] = useSearchParams();

  const stageParamValues = useMemo(() => searchParams.getAll("stage"), [searchParams]);
  const stageTabSignature = useMemo(() => stageParamValues.slice().sort().join(","), [stageParamValues]);

  const stages = useMemo(
    () => stageParamValues.map(parseToInquiryStage).filter((stage): stage is InquiryStage => Boolean(stage)),
    [stageParamValues]
  );

  const serviceIds = useMemo(() => searchParams.getAll("service_id"), [searchParams]);
  const stageStartFrom = searchParams.get("stage_start_from") ?? undefined;
  const stageStartTo = searchParams.get("stage_start_to") ?? undefined;

  const {
    queryKey,
    data: { inquiries, totalPages, stagesCount = EMPTY_STAGES_COUNT } = {
      inquiries: [],
      totalPages: 0,
      stagesCount: EMPTY_STAGES_COUNT
    },
    isFetching
  } = useInquiryList({
    search,
    stages,
    serviceIds,
    stageStartFrom,
    stageStartTo,
    sortBy,
    limit,
    pageIndex
  });
  const firstInquiry = inquiries[0];

  const { activeCenterId, onChangeActiveCenter } = useAccessControl();
  const { activeConstants } = useCenterConstants(activeCenterId);

  const serviceFilterOptions = useMemo(
    () =>
      (activeConstants.activeServices ?? []).map(s => ({
        label: s.name,
        value: String(s.value)
      })),
    [activeConstants.activeServices]
  );

  /** Stages that exist at the center (`stagesCount` from API is not filtered by current list filters). */
  const stageFilterOptions = useMemo(() => {
    const selectedStages = stageParamValues.map(parseToInquiryStage).filter((s): s is InquiryStage => Boolean(s));
    const withInquiries = new Set(inquiryStages.filter(s => (stagesCount[s] ?? 0) > 0));
    selectedStages.forEach(s => withInquiries.add(s));
    let ordered = inquiryStages.filter(s => withInquiries.has(s));
    if (ordered.length === 0) {
      ordered = [...inquiryStages];
    }
    return ordered.map(s => ({ label: s, value: s }));
  }, [stagesCount, stageParamValues]);

  const patchSearchParams = useCallback(
    (fn: (p: URLSearchParams) => void) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          fn(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const onServiceFilterChange = useCallback(
    (values: string[]) => {
      patchSearchParams(p => {
        p.delete("service_id");
        values.forEach(id => p.append("service_id", id));
      });
      setPageIndex(0);
    },
    [patchSearchParams]
  );

  const onStageFilterChange = useCallback(
    (values: string[]) => {
      const valid = values.map(parseToInquiryStage).filter((s): s is InquiryStage => Boolean(s));
      patchSearchParams(p => {
        p.delete("stage");
        valid.forEach(s => p.append("stage", s));
      });
      setPageIndex(0);
    },
    [patchSearchParams]
  );

  const onDateRangeApply = useCallback(
    (from: string | undefined, to: string | undefined) => {
      patchSearchParams(p => {
        if (from) {
          p.set("stage_start_from", from);
        } else {
          p.delete("stage_start_from");
        }
        if (to) {
          p.set("stage_start_to", to);
        } else {
          p.delete("stage_start_to");
        }
      });
      setPageIndex(0);
    },
    [patchSearchParams]
  );

  const selectInquiry = (inquiry?: Inquiry) => {
    setSelectedInquiry(inquiry);
    if (inquiry) {
      onInquirySelect?.(inquiry);
    }
  };

  useInquiryStatus(inquiries, queryKey);
  const { data: preselectedInquiry } = useInquirySummary(preselectedInquiryId);

  const { centers } = useCenters();
  const navigate = useNavigate();
  const { profile } = useMyProfile();
  const queryClient = useQueryClient();
  const { getToken } = useAccessToken();
  const confirmModal = useRef<ConfirmHandle>(null);
  const [checkPreselectedInquiry, setCheckPreselectedInquiry] = useState<boolean>(true);

  useEffect(() => {
    if (preselectedInquiryId) {
      setCheckPreselectedInquiry(true);
    }
  }, [preselectedInquiryId]);

  useEffect(() => {
    if (!isFetching && inquiries && !hasInitialData && !preselectedInquiry) {
      if (!selectedInquiry) {
        selectInquiry(firstInquiry);
      } else {
        const inquiryToSelect = inquiries.find(inquiry => inquiry.id === selectedInquiry.id);
        if (!inquiryToSelect) {
          selectInquiry(firstInquiry);
        } else {
          setSelectedInquiry(inquiryToSelect);
        }
      }
      setHasInitialData(true);
    }
  }, [isFetching, inquiries, hasInitialData]);

  useEffect(() => {
    if (preselectedInquiry && checkPreselectedInquiry) {
      setCheckPreselectedInquiry(false);
      const { centerId } = preselectedInquiry;

      if (activeCenterId === centerId) {
        setSelectedInquiry(preselectedInquiry);
        onInquirySelect?.(preselectedInquiry);
        return;
      }

      if (!centers?.some(center => center.id === centerId)) {
        window.toasts.add({
          content: "You do not have permission to access this inquiry's center",
          type: "danger"
        });
        navigate("/inquiry");
        selectInquiry(firstInquiry);
        return;
      }

      prefetchPermissions(centerId, profile?.id);
      confirmModal?.current?.confirm(
        () => {
          onChangeActiveCenter?.(centerId);
          setSelectedInquiry(preselectedInquiry);
          onInquirySelect?.(preselectedInquiry);
        },
        () => {
          navigate("/inquiry");
          selectInquiry(firstInquiry);
        }
      );
    }
  }, [preselectedInquiry, firstInquiry]);

  useEffect(() => {
    setHasInitialData(false);
  }, [stages]);

  useEffect(() => {
    if (selectedInquiry && inquiries?.length) {
      const reloadedSelectedInquiry = inquiries.find(i => i.id === selectedInquiry.id);
      if (reloadedSelectedInquiry) {
        setSelectedInquiry(reloadedSelectedInquiry);
        onInquirySelect?.(reloadedSelectedInquiry);
      }
    }
  }, [inquiries]);

  const prefetchPermissions = (centerId: string, staffMemberId?: string) => {
    if (centerId && staffMemberId) {
      queryClient.prefetchQuery({
        queryKey: ["staffMemberPermissionsData", centerId, staffMemberId],
        queryFn: async () => getStaffMemberPermissions(await getToken(), centerId, staffMemberId)
      });
    }
  };

  const startDateColumnName = "Status Start Date";

  const handleSortByChange = useCallback(
    (newSort: string) => {
      setPageIndex(0);
      commitSortBy(oldSort => {
        if (oldSort === newSort) {
          return `-${newSort}`;
        }
        return newSort;
      });
    },
    [commitSortBy]
  );

  const inquiryListHeaders: SortableTableHeader<Inquiry>[] = useMemo(() => {
    const studentHeader: SortableTableHeader<Inquiry> = {
      renderKey: "student",
      name: "Student",
      headerContent: headerWithFilter("Student", null),
      tableDataCellProps: { isPrimary: true },
      render: item => <a href="#">{item.studentName}</a>
    };

    const serviceHeader: SortableTableHeader<Inquiry> = {
      key: "service",
      name: "Service",
      headerContent: headerWithFilter(
        "Service",
        <MultiCheckboxColumnFilter
          key="svc-filter"
          options={serviceFilterOptions}
          selected={serviceIds}
          onSelectedChange={onServiceFilterChange}
          aria-label="Service column: filter"
          optionsScrollMaxHeight="12rem"
          panelMinWidthHeaderThIndex={0}
        />
      )
    };

    const statusHeader: SortableTableHeader<Inquiry> = {
      key: "stage" as keyof Inquiry,
      name: "Status",
      headerContent: headerWithFilter(
        "Status",
        <MultiCheckboxColumnFilter
          key="stg-filter"
          options={stageFilterOptions}
          selected={stageParamValues}
          onSelectedChange={onStageFilterChange}
          aria-label="Status column: filter"
          optionsScrollMaxHeight="12rem"
          panelMinWidthHeaderThIndex={1}
          panelMinWidthMatchScale={0.9}
        />
      )
    };

    const dateHeader: SortableTableHeader<Inquiry> = {
      renderKey: "stageStartDate",
      name: startDateColumnName,
      render: item => formatDateString(item.stageStartDate),
      headerContent: headerWithFilter(
        startDateColumnName,
        <StageStartDateRangeFilter
          key="date-filter"
          fromValue={stageStartFrom}
          toValue={stageStartTo}
          onApply={onDateRangeApply}
          aria-label="Status start date: filter"
        />
      )
    };

    return [studentHeader, serviceHeader, statusHeader, dateHeader];
  }, [
    serviceFilterOptions,
    serviceIds,
    onServiceFilterChange,
    stageFilterOptions,
    stageParamValues,
    onStageFilterChange,
    stageStartFrom,
    stageStartTo,
    onDateRangeApply
  ]);

  const handleSelectItem = (item: Inquiry) => {
    setSelectedInquiry(item);
    onInquirySelect?.(item);
  };

  useEffect(() => {
    setPageIndex(0);
  }, [search, limit, sortBy, serviceIds, stageStartFrom, stageStartTo, stageTabSignature]);

  useEffect(() => {
    if (sortControlled && onSortByChange) {
      onSortByChange("student_last_name");
    } else {
      setSortByInternal("student_last_name");
    }
    onStatusChange?.();
  }, [stageTabSignature, onStatusChange, sortControlled, onSortByChange]);

  const paginationOptions = {
    itemsPerPage: limit,
    onPageChange: (pageNumber: number) => setPageIndex(pageNumber - 1)
  };

  const useInquiryPaginatedSorter: UsePaginatedSorterHook<Inquiry> = ({
    data,
    paginationOptions: { onPageChange } = {},
    isLoading
  }) => {
    const [sortConfig, setSortConfig] = useState<SortConfig<Inquiry>>({
      key: null,
      secondaryKey: null,
      direction: "asc"
    });

    const { renderPaginator, setCurrentPage } = usePaginator({
      onPageChange,
      isLoading,
      numberOfPages: totalPages
    });

    useEffect(() => {
      const descending = sortBy.startsWith("-");
      const raw = descending ? sortBy.slice(1) : sortBy;
      let key: keyof Inquiry | null = null;
      if (raw === "service") {
        key = "service";
      } else if (raw === "stage") {
        key = "stage";
      }
      setSortConfig({
        key,
        secondaryKey: null,
        direction: descending ? "des" : "asc"
      });
    }, [sortBy]);

    const requestSort = ({ key, sortOptions }: SortableTableHeader<Inquiry>) => {
      let direction: SortDirection = "asc";
      if (sortConfig.key === key && sortConfig.direction === "asc") {
        direction = "des";
      }
      setSortConfig({ key: key ?? null, secondaryKey: null, direction });
      handleSortByChange(sortOptions?.customSortKey ?? (key as string) ?? "");
      setCurrentPage(1);
    };

    return {
      paginatedData: data,
      requestSort,
      renderPaginator
    };
  };

  return (
    <>
      <SortableTable
        className="inquiry-list-table"
        data-inquiry-columns="4"
        tableCollapseScreenSize="sm"
        headers={inquiryListHeaders}
        data={inquiries}
        activeItem={selectedInquiry}
        paginationOptions={paginationOptions}
        usePaginatedSorter={useInquiryPaginatedSorter}
        isLoading={isFetching}
        onRowClick={handleSelectItem}
        tableRef={tableRef}
        uniqueIdKey="id"
        printCaption="Inquiries"
      />
      <Confirm
        ref={confirmModal}
        title="Confirm"
        message={`Do you want to change your currently active center to view this inquiry?`}
      />
    </>
  );
};
